import type { QueryViewMode } from '../../core/types/workspaceTypes';
import type { QueryViewModeSource, QueryViewModeTarget } from './useQueryViewMode';

/**
 * Each mounted Query tab's SQL | Visual switch, for the entry points that act
 * on "the active Query tab" without holding it — Ctrl/Cmd+Shift+V, the
 * Command Palette, More ▾ → Visual Query Editor (`openVqeFromActiveTab`).
 * `QueryEditorTabContent` registers one per tab; the switch itself (flush,
 * caret statement, focus, analytics) stays in the tab's `useQueryViewMode`.
 * A request for a tab that has not registered yet (a tab that is still
 * mounting) waits for it rather than writing the view past the tab's hook.
 */
export type QueryViewModeController = {
  readonly isVisual: () => boolean;
  readonly setViewMode: (mode: QueryViewMode, source: QueryViewModeSource, target?: QueryViewModeTarget) => void;
};

type ViewModeRequest = {
  readonly mode: QueryViewMode | 'toggle';
  readonly source: QueryViewModeSource;
};

const controllers = new Map<string, QueryViewModeController>();
const pending = new Map<string, ViewModeRequest>();

function run(controller: QueryViewModeController, request: ViewModeRequest): void {
  const mode = request.mode === 'toggle' ? (controller.isVisual() ? 'sql' : 'visual') : request.mode;
  controller.setViewMode(mode, request.source);
}

export function registerQueryViewModeController(tabId: string, controller: QueryViewModeController): () => void {
  controllers.set(tabId, controller);
  const queued = pending.get(tabId);
  if (queued) {
    pending.delete(tabId);
    run(controller, queued);
  }
  return () => {
    if (controllers.get(tabId) === controller) controllers.delete(tabId);
  };
}

export function queryViewModeController(tabId: string): QueryViewModeController | undefined {
  return controllers.get(tabId);
}

/** Switch the tab now, or as soon as it registers (the latest request wins). */
export function requestQueryViewMode(tabId: string, mode: QueryViewMode | 'toggle', source: QueryViewModeSource): void {
  const controller = controllers.get(tabId);
  if (controller) run(controller, { mode, source });
  else pending.set(tabId, { mode, source });
}
